import { darken } from "@mui/material";
import { Group } from "@visx/group";
import { Pie } from "@visx/shape";
import type { PieProps } from "@visx/shape/lib/shapes/Pie";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";

import { Square } from "@citrine/atoms/square";
import { Tooltip } from "@citrine/atoms/tooltip";

import { useDataVizColorVariants } from "#src/hooks/use-data-viz-colors";
import { useElementSize } from "#src/hooks/use-element-size";

import * as sx from "./pie-chart.sx";

/** The fraction of the chart height/width that we want to be empty (center of the donut) */
const hole = 0.6;

/** Reusable visx Pie/Donut chart */
export function PieChart<Datum>({
  centerContent,
  className,
  data,
  getFill: getFillProp,
  getArcDataTestid,
  getArcKey,
  hoveredContent,
  hoveredFullWidth,
  pieSort,
  pieValue,
}: Required<Pick<PieProps<Datum>, "data" | "pieValue">> &
  /** By default, slices are ordered from big to small according to the `pieValue` function */
  Pick<PieProps<Datum>, "pieSort"> & {
    centerContent?: ReactNode;
    className?: string;
    /** `getFill` allows specifying custom coloring */
    getFill?: (datum: Datum, index: number) => string;
    getArcDataTestid?: (datum: Datum) => string;
    getArcKey?: (datum: Datum) => string;
    /** Making `hoveredContent` required since we don't have regular labels */
    hoveredContent: (props: Datum) => ReactNode;
    hoveredFullWidth?: boolean;
  }) {
  const getFill = useGetFill(getFillProp);

  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const { width, height } = useElementSize(containerRef);

  // Take the smaller dimension and use that for both the height and width
  const size = height < width ? height : width;

  const [hoverData, setHoverData] = useState<Datum | null>(null);

  return (
    <Square className={className} ref={setContainerRef}>
      <svg width={size} height={size}>
        {/* Put center content in a foreignObject so we can position it relative
              to the pie. Absolute positioning without the foreignObject requires
              disabling pointer events to make the pie chart interactions work,
              so the foreignObject approach is better. */}
        {centerContent ? (
          <foreignObject
            x={(size / 2) * (1 - hole)}
            width={size * hole}
            y={(size / 2) * (1 - hole)}
            height={size * hole}
          >
            <sx.CenterContentContainer>
              {centerContent}
            </sx.CenterContentContainer>
          </foreignObject>
        ) : null}

        <Group top={size / 2} left={size / 2}>
          <Pie<Datum>
            data={data}
            endAngle={Math.PI}
            innerRadius={(size / 2) * hole}
            outerRadius={size / 2}
            padAngle={0.01}
            pieSort={pieSort}
            pieValue={pieValue}
            startAngle={-Math.PI}
          >
            {(pie) => {
              return pie.arcs.map((arc, index) => {
                const [centroidX, centroidY] = pie.path.centroid(arc);
                const color = getFill(arc.data, index);

                return (
                  <Group
                    key={getArcKey?.(arc.data) ?? `arc-${index}`}
                    onMouseLeave={() => setHoverData(null)}
                    onMouseEnter={() => setHoverData(arc.data)}
                  >
                    {/* put the tooltip inside the group to simplify onMouseLeave */}
                    {hoverData === arc.data ? (
                      <foreignObject x={centroidX} y={centroidY}>
                        <Tooltip
                          open
                          fullWidth={hoveredFullWidth}
                          placement="right"
                          title={hoveredContent(arc.data)}
                        >
                          <div />
                        </Tooltip>
                      </foreignObject>
                    ) : null}

                    <sx.PieWedge
                      data-testid={
                        getArcDataTestid?.(arc.data) ?? "pie-chart-wedge"
                      }
                      d={pie.path(arc) ?? undefined}
                      fill={hoverData === arc.data ? darken(color, 0.3) : color}
                    />
                  </Group>
                );
              });
            }}
          </Pie>
        </Group>
      </svg>
    </Square>
  );
}

function useGetFill<Datum>(
  getFillProp?: (datum: Datum, index: number) => string
) {
  const defaultColors = useDataVizColorVariants();

  const defaultGetFill = useCallback(
    (_datum: Datum, index: number) =>
      defaultColors[index % defaultColors.length],
    [defaultColors]
  );

  return getFillProp ?? defaultGetFill;
}
